// Small building blocks shared by the detail pages (hosting, domain, email, backups).
import { useState } from 'react';
import Modal, { Field } from '../../components/Modal.jsx';
import { Icon } from '../../components/Icon.jsx';
import { errorMessage } from '../../lib/errors.js';
import { apiBase } from '../../lib/config.js';
import { ApiError } from '../../lib/api.js';
import { useT } from '../../lib/i18n.jsx';
import { localeFor } from '../../lib/format.js';

// A `.field` block whose label is bound to its control.
export function FormField({ label, hint, error, children }) {
  return (
    <Field className="field" label={<span className="field-label">{label}</span>} hint={hint} error={error}>
      {children}
    </Field>
  );
}

// Danger-styled (or neutral) confirmation dialog that can run an async action
// and shows its error inline instead of closing.
export function ConfirmDialog({ title, body, cta, danger = true, lang, onClose, onConfirm }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async () => {
    setBusy(true);
    setErr('');
    try {
      await onConfirm();
    } catch (e) {
      setErr(errorMessage(e, { lang }));
      setBusy(false);
    }
  };
  return (
    <Modal
      onClose={busy ? () => {} : onClose}
      title={title}
      description={body}
      maxWidth={460}
      className={danger ? 'modal--danger' : ''}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
            {t('g.cancel')}
          </button>
          <button type="button" className={danger ? 'btn-danger' : 'btn-primary'} onClick={run} disabled={busy}>
            {busy ? t('detailUi.working') : cta}
          </button>
        </>
      }
    >
      {err && (
        <div className="modal-body">
          <div className="login-error" role="alert">{err}</div>
        </div>
      )}
    </Modal>
  );
}

// Loading / not found / error states for a detail page, always with a way back.
export function DetailState({ lang, loading, error, backLabel, onBack, notFoundTitle, onRetry }) {
  const t = useT();
  if (loading) {
    return (
      <main className="page">
        <div className="page-loader">{t('common.loading')}</div>
      </main>
    );
  }
  const notFound = !error || error.status === 404 || error.status === 403;
  return (
    <main className="page">
      <button className="back-link" onClick={onBack}>
        <Icon name="arrow-left" size={14} />
        <span style={{ marginLeft: 4 }}>{backLabel}</span>
      </button>
      <div className="state-card" role={notFound ? undefined : 'alert'}>
        <h3>{notFound ? notFoundTitle || t('detailUi.notFound') : t('detailUi.loadFailed')}</h3>
        <p>{notFound ? errorMessage({ status: 404 }, { lang }) : errorMessage(error, { lang })}</p>
        {!notFound && onRetry && (
          <button className="btn-ghost" style={{ marginTop: 14 }} onClick={onRetry}>
            {t('detailUi.retry')}
          </button>
        )}
      </div>
    </main>
  );
}

// Inline error banner used inside cards and modals.
export function InlineError({ error, lang }) {
  if (!error) return null;
  return (
    <div className="login-error" role="alert" style={{ marginBottom: 12 }}>
      {typeof error === 'string' ? error : errorMessage(error, { lang })}
    </div>
  );
}

// Fetches a protected file with the bearer token and hands it to the browser as a download.
export async function downloadWithAuth(path, filename) {
  const token = localStorage.getItem('token');
  let r;
  try {
    r = await fetch(`${apiBase()}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  } catch {
    throw new ApiError(0, '', null);
  }
  if (!r.ok) {
    const payload = await r.json().catch(() => null);
    throw new ApiError(r.status, r.statusText, payload);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function formatBytes(bytes, lang = 'nl') {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const s = new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: i === 0 ? 0 : 1 }).format(v);
  return `${s} ${units[i]}`;
}
